import React, { useState, useEffect, useCallback } from 'react';
import { connect, useSelector } from 'react-redux';
import { Modal, Button, ButtonGroup, Col, Row, Form, Alert } from '../../../ui-kit';
import { IconExclamationTriangle } from '../../../ui-kit/icons';
import { operations as overtimeCarryOverOperations } from '../../../../state/modules/overtime-carry-overs';
import moment from 'moment';
import GenericAvailabilityCodeSelector from '../../../general/AvailabilityCodeSelector';
import { operations as availabilityCodesOperations } from '../../../../state/modules/availability-codes';
import { selectors as availabilityCodesSelectors } from '../../../../state/modules/availability-codes';
import { Toast, ToastContainer } from 'react-bootstrap5';

const NewOvertimeCarryOverForm = props => {
  const [id, setId] = useState(null);
  const currentOvertimeCarryOver = useSelector(state => state.overtimeCarryOvers.byId[id]);
  const [showToast, setShowToast] = useState(false);

  const fromPeriod = () => {
    // Get the year for which the overtime carry over will apply
    const year = moment(props.time).year();

    // Calculate the anchor date for the given year. If there is no
    // anchor date, we will use the beginning of the provided period
    const anchor = props.contract.contract_type.anchor_date
      ? moment(props.contract.contract_type.anchor_date).year(year)
      : moment(props.time).startOf(props.periodType);

    const periodStart = moment.max(moment(props.contract.start_time), moment(props.time).startOf(props.periodType));

    return moment.max(moment(anchor), moment(periodStart)).startOf('day').add(9, 'hours');
  };

  const initialCarryOver = {
    is_deduction: false,
    shifts: '',
    hours: '',
    from_period: fromPeriod(),
    to_period: null,
    comments: '',
    availability_code_id: null,
    contract_id: props.contract.id
  };

  useEffect(() => {
    props.fetchAvailabilityCodes({ params: { presentation_type: 'basic' } });
  }, []);

  useEffect(() => {
    if (!props.overtimeCarryOversById) return;

    const objects = Object.values(props.overtimeCarryOversById);

    const transientObject = objects.find(o => o.transient);
    setId(transientObject?.id);
  }, [props.overtimeCarryOversById]);

  useEffect(() => {
    if (currentOvertimeCarryOver?.transient) return;

    if (props.showCarryOverForm) props.createNewOvertimeCarryOver(initialCarryOver);
  }, [props.showCarryOverForm]);

  const handleSaveOvertimeCarryOver = () => {
    props.saveOvertimeCarryOver(id).then(() => setShowToast(true));
    props.setShowCarryOverForm(false);
    // Trigger a forced contract statistics fetch to update
    // values (Temporarily disabled since we are doing a full page reload)
    // TODO: remove page refresh and trigger a JS event to update the non-react
    // contract stats as well
    // props.loadContractStatistics(true);
    location.reload();
  };

  const handleChangeAttribute = useCallback(
    (attributeName, value) => {
      props.updateField(id, { [attributeName]: value });
    },
    [currentOvertimeCarryOver]
  );

  const updateAvailabilityCode = availabilityCodes => {
    handleChangeAttribute('availability_code_id', availabilityCodes[0]);
  };

  const periodFormats = {
    year: 'YYYY',
    quarter: 'Q YYYY',
    month: 'MMM YYYY',
    week: 'WW YYYY'
  };

  const periodDecorators = {
    year: '',
    quarter: 'Q',
    month: '',
    week: 'W'
  };

  const periodFormat = periodFormats[props.periodType] || 'YYYY';
  const periodDecorator = periodDecorators[props.periodType] || '';

  let next_period = currentOvertimeCarryOver?.from_period.clone().add(1, props.periodType).format(periodFormat);
  next_period = `${periodDecorator}${next_period}`;

  let period = currentOvertimeCarryOver?.from_period.format(periodFormat);
  period = `${periodDecorator}${period}`;

  let primaryAction = currentOvertimeCarryOver?.is_deduction ? 'add_to_period' : 'deduct_from_period';
  let primaryAndSecondaryAction = currentOvertimeCarryOver?.is_deduction ? 'add_and_deduct' : 'deduct_and_add';

  const expiredContract = props.contract.end_time && moment().isAfter(props.contract.end_time);

  return (
    <>
      <ToastContainer className="position-fixed top-0 end-0 p-3">
        <Toast show={showToast} delay={5000} onClose={() => setShowToast(false)} bg="success" autohide>
          <Toast.Header>{I18n.t('activerecord.models.reporting/contracts/overtime_carry_over')}</Toast.Header>
          <Toast.Body>{I18n.t('changes_saved_successfully')}</Toast.Body>
        </Toast>
      </ToastContainer>
      <Modal size="lg" show={props.showCarryOverForm} onHide={() => props.setShowCarryOverForm(false)}>
        <Modal.Header closeButton>
          <Modal.Title> {I18n.t('activerecord.models.reporting/contracts/overtime_carry_over')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {expiredContract && (
            <Alert variant="warning">
              <Row>
                <Col sm={1} className="d-flex align-items-center justify-content-center">
                  <IconExclamationTriangle className="text-warning fa-2x" />
                </Col>
                <Col>{I18n.t('reporting.contracts.overtime_carry_overs.warning_expired_contract')}</Col>
              </Row>
            </Alert>
          )}
          <Form horizontal>
            <Form.Group as={Row} className="mb-3">
              <Col sm={3} />
              <Col sm={9}>
                <a
                  href="https://help.fireservicerota.com/en/article/how-does-the-carryover-mechanism-work-in-contracts"
                  target={'_blank'}
                  rel="noreferrer noopener"
                >
                  {I18n.t('reporting.contracts.overtime_carry_overs.help')}
                </a>
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Col sm={3} />
              <Col sm={9}>
                <ButtonGroup className="d-flex flex-wrap">
                  <Button
                    variant={currentOvertimeCarryOver?.is_deduction ? 'outline-primary' : 'primary'}
                    onClick={() => handleChangeAttribute('is_deduction', false)}
                  >
                    {I18n.t('reporting.contracts.overtime_carry_overs.deduct')}
                  </Button>
                  <Button
                    variant={currentOvertimeCarryOver?.is_deduction ? 'primary' : 'outline-primary'}
                    onClick={() => handleChangeAttribute('is_deduction', true)}
                  >
                    {I18n.t('reporting.contracts.overtime_carry_overs.add')}
                  </Button>
                </ButtonGroup>
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                {I18n.t('reports.group_reports.standby_duty_hours.mode.hours')}
              </Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="text"
                  value={currentOvertimeCarryOver?.hours?.toString() || ''}
                  onChange={e => handleChangeAttribute('hours', e.target.value)}
                  placeholder={'0:00'}
                />
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                {I18n.t('reports.group_reports.standby_duty_hours.mode.shifts')}
              </Form.Label>
              <Col sm={9}>
                <Form.Control
                  type="number"
                  value={currentOvertimeCarryOver?.shifts?.toString() || ''}
                  onChange={e => handleChangeAttribute('shifts', Number.parseFloat(e.target.value))}
                  placeholder={'0'}
                />
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Col sm={3} />
              <Col sm={9}>
                <ButtonGroup className="d-flex flex-wrap">
                  <Button
                    variant={currentOvertimeCarryOver?.to_period ? 'outline-primary' : 'primary'}
                    onClick={() => handleChangeAttribute('to_period', null)}
                  >
                    {I18n.t(`reporting.contracts.overtime_carry_overs.${primaryAction}`, {
                      period
                    })}
                  </Button>
                  <Button
                    variant={currentOvertimeCarryOver?.to_period ? 'primary' : 'outline-primary'}
                    onClick={() =>
                      handleChangeAttribute(
                        'to_period',
                        currentOvertimeCarryOver?.from_period
                          .clone()
                          .add(1, props.periodType || 'year')
                          .startOf('day')
                      )
                    }
                  >
                    {I18n.t(`reporting.contracts.overtime_carry_overs.${primaryAndSecondaryAction}`, {
                      period,
                      next_period
                    })}
                  </Button>
                </ButtonGroup>
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={3}>
                {I18n.t('activerecord.models.schedule/availability_code.other')}
              </Form.Label>
              <Col sm={9}>
                <GenericAvailabilityCodeSelector
                  loading={!props.availabilityCodesLoaded}
                  availabilityCodes={props.availabilityCodes}
                  onChange={updateAvailabilityCode}
                  multi={false}
                />
              </Col>
            </Form.Group>
            <Form.Group as={Row}>
              <Form.Label column sm={3}>
                {I18n.t('comments')}
              </Form.Label>
              <Col sm={9}>
                <Form.Control
                  as="textarea"
                  type="text"
                  value={currentOvertimeCarryOver?.comments || ''}
                  onChange={e => handleChangeAttribute('comments', e.target.value)}
                />
              </Col>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleSaveOvertimeCarryOver} variant="success">
            {I18n.t('save')}
          </Button>
          <Button onClick={() => props.setShowCarryOverForm(false)}>{I18n.t('close')}</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const mapStateToProps = state => ({
  overtimeCarryOversById: state.overtimeCarryOvers.byId,
  availabilityCodesLoaded: availabilityCodesSelectors.initialLoadComplete(state),
  availabilityCodes: availabilityCodesSelectors.selectForBookkeeping(state),
  user: state.users.byId
});

const mapDispatchToProps = {
  createNewOvertimeCarryOver: overtimeCarryOverOperations.createNew,
  updateField: overtimeCarryOverOperations.updateFields,
  saveOvertimeCarryOver: overtimeCarryOverOperations.storeInBackend,
  fetchAvailabilityCodes: availabilityCodesOperations.fetch
};

export default connect(mapStateToProps, mapDispatchToProps)(NewOvertimeCarryOverForm);
